import { supabase } from './supabaseClient';
import { describeError } from './errorMessage';
import {
    ProjectMedia,
    mapProjectMediaRow,
    sortProjectMedia,
    groupProjectMedia,
    validateReferenceImageFile,
    referenceImageStoragePath,
    nextSortOrder,
    CAPTION_MAX,
} from './projectReferenceLogic';

/**
 * PROJEKTBILDER — Tabelle `project_media` (Nr. 99) + Bucket `project-assets`.
 *
 * Anlass (Betreiber 12.09.2026): Referenzfotos je Projekt fuer Referenzblatt
 * und Social Media. Eine Zeile je Datei; die Datei liegt UNVERAENDERT im
 * oeffentlichen Bucket unter `<projectId>/referenz/<uuid>.<ext>`.
 *
 * Warum NICHT uploadAsset aus cloudService: das nimmt Base64, hasht den
 * Inhalt und laedt mit upsert. Ein 30-MB-Foto als Base64-String durch den
 * Speicher zu ziehen ist unnoetig — die Datei geht hier als Blob direkt hoch.
 *
 * FAIL-OPEN: solange Nr. 99 nicht gefahren ist, liefern die Lesewege leere
 * Listen mit `tabelleFehlt = true` (42P01/PGRST205); die Oberflaechen zeigen
 * den Hinweis und sperren den Upload. Schreibwege werfen mit Klartext.
 *
 * Alle Fehler gehen als `Error(describeError(e))` hinaus.
 */

const TABLE = 'project_media';
const BUCKET = 'project-assets';
const COLS = 'id, project_id, storage_pfad, dateiname, mime_type, groesse_bytes, breite, hoehe, titelbild, sort_order, bildunterschrift, hochgeladen_von, hochgeladen_von_id, company_name, created_at, updated_at';

export const MEDIA_TABLE_MISSING_TEXT = 'Die Tabelle für Projektbilder fehlt noch (Migration Nr. 99, project_media.sql). Bitte den Betreiber informieren.';

// ─── Fehler ─────────────────────────────────────────────────────────────────

const errorCode = (e: unknown): string => {
    const c = (e as { code?: unknown })?.code;
    return typeof c === 'string' ? c : '';
};
/** PostgREST liefert je nach Schema-Cache 42P01 oder PGRST205. */
const istFehlendeTabelle = (e: unknown): boolean => {
    const code = errorCode(e);
    return code === 'PGRST205' || code === '42P01';
};
const istDublette = (e: unknown): boolean => errorCode(e) === '23505';

const werfe = (e: unknown): never => {
    if (istFehlendeTabelle(e)) throw new Error(MEDIA_TABLE_MISSING_TEXT);
    throw new Error(describeError(e));
};

// ─── Helfer ─────────────────────────────────────────────────────────────────

export const publicUrlFor = (path: string): string =>
    supabase.storage.from(BUCKET).getPublicUrl(path).data.publicUrl;

const mapRows = (rows: unknown): ProjectMedia[] =>
    sortProjectMedia(((rows as Record<string, unknown>[]) || []).map(r => mapProjectMediaRow(r, publicUrlFor)));

const randomToken = (): string => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID().replace(/-/g, '');
    }
    return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
};

/** Firmenname des angemeldeten Nutzers — best effort (Anzeige, kein Riegel). */
const eigenerFirmenname = async (): Promise<string | null> => {
    try {
        const { data: auth } = await supabase.auth.getUser();
        const userId = auth?.user?.id;
        if (!userId) return null;
        const { data } = await supabase.from('profiles').select('company_name').eq('id', userId).maybeSingle();
        const name = (data as { company_name?: string } | null)?.company_name;
        return name?.trim() ? name.trim() : null;
    } catch {
        return null;
    }
};

/** Angemeldeter Nutzer (id, Anzeigename) — best effort. */
const eigenerNutzer = async (): Promise<{ id: string | null; name: string | null }> => {
    try {
        const { data: auth } = await supabase.auth.getUser();
        const user = auth?.user;
        if (!user?.id) return { id: null, name: null };
        const { data } = await supabase.from('profiles').select('first_name, last_name').eq('id', user.id).maybeSingle();
        const p = data as { first_name?: string; last_name?: string } | null;
        const name = ([p?.first_name, p?.last_name].filter(Boolean).join(' ') || user.email || '').trim();
        return { id: user.id, name: name || null };
    } catch {
        return { id: null, name: null };
    }
};

/** Breite/Hoehe der Datei im Browser lesen; null, wenn der Browser es nicht kann. */
export const readImageDimensions = async (file: Blob): Promise<{ width: number; height: number } | null> => {
    try {
        if (typeof createImageBitmap === 'function') {
            const bmp = await createImageBitmap(file);
            const dims = { width: bmp.width, height: bmp.height };
            bmp.close?.();
            return dims;
        }
    } catch {
        // Fallback unten
    }
    if (typeof Image === 'undefined' || typeof URL === 'undefined' || !URL.createObjectURL) return null;
    return new Promise(resolve => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => { URL.revokeObjectURL(url); resolve({ width: img.naturalWidth, height: img.naturalHeight }); };
        img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
        img.src = url;
    });
};

// ─── Lesen ──────────────────────────────────────────────────────────────────

export interface MediaListe {
    items: ProjectMedia[];
    /** Nr. 99 noch nicht gefahren — Oberflaeche zeigt den Hinweis, Upload gesperrt. */
    tabelleFehlt: boolean;
}

/** Galerie EINES Projekts in Anzeige-Reihenfolge. */
export const listProjectMedia = async (projectId: string): Promise<MediaListe> => {
    if (!projectId) return { items: [], tabelleFehlt: false };
    const { data, error } = await supabase
        .from(TABLE)
        .select(COLS)
        .eq('project_id', projectId)
        .order('titelbild', { ascending: false })
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true });
    if (error) {
        if (istFehlendeTabelle(error)) return { items: [], tabelleFehlt: true };
        return werfe(error);
    }
    return { items: mapRows(data), tabelleFehlt: false };
};

export interface MediaAlle {
    byProject: Map<string, ProjectMedia[]>;
    tabelleFehlt: boolean;
}

/**
 * Alle Bilder, die der Nutzer sehen darf (RLS filtert nach Projektzugang;
 * Admins sehen alles), gruppiert je Projekt. Fuer Kataloge & Vorlagen →
 * Projektbilder und
 * das Referenzblatt — EINE Abfrage, nicht eine je Projekt.
 */
export const listAllProjectMedia = async (): Promise<MediaAlle> => {
    const { data, error } = await supabase
        .from(TABLE)
        .select(COLS)
        .order('created_at', { ascending: true })
        .limit(5000);
    if (error) {
        if (istFehlendeTabelle(error)) return { byProject: new Map(), tabelleFehlt: true };
        return werfe(error);
    }
    return { byProject: groupProjectMedia(mapRows(data)), tabelleFehlt: false };
};

// ─── Schreiben ──────────────────────────────────────────────────────────────

/**
 * Datei hochladen und Zeile anlegen. Das erste Bild eines Projekts wird
 * automatisch Titelbild. Scheitert der Tabelleneintrag, wird das gerade
 * hochgeladene Objekt wieder entfernt (kein verwaistes Objekt durch einen
 * halben Upload).
 */
export const uploadProjectMedia = async (
    file: File,
    projectId: string,
    existing: ProjectMedia[],
): Promise<ProjectMedia> => {
    const fehler = validateReferenceImageFile(file);
    if (fehler) throw new Error(fehler);
    if (!projectId) throw new Error('Das Projekt ist noch nicht gespeichert — bitte zuerst speichern, dann Bilder hochladen.');

    const path = referenceImageStoragePath(projectId, file.type, randomToken());
    const { error: upErr } = await supabase.storage
        .from(BUCKET)
        .upload(path, file, { contentType: file.type, upsert: false, cacheControl: '3600' });
    if (upErr) {
        const text = (upErr as { message?: string })?.message || String(upErr);
        if (/payload too large|exceeded the maximum allowed size|413/i.test(text)) {
            throw new Error('Der Speicher hat die Datei als zu groß abgelehnt (Projektlimit in Supabase). Bitte ein kleineres Bild wählen oder das Upload-Limit im Supabase-Dashboard anheben.');
        }
        throw new Error(`Upload fehlgeschlagen: ${text}`);
    }

    const [dims, companyName, nutzer] = await Promise.all([readImageDimensions(file), eigenerFirmenname(), eigenerNutzer()]);
    const wirdTitelbild = existing.length === 0 || !existing.some(i => i.titelbild);

    const zeile = {
        project_id: projectId,
        storage_pfad: path,
        dateiname: file.name.slice(0, 255) || 'bild',
        mime_type: file.type,
        groesse_bytes: file.size,
        breite: dims?.width ?? null,
        hoehe: dims?.height ?? null,
        titelbild: wirdTitelbild,
        sort_order: nextSortOrder(existing),
        hochgeladen_von: nutzer.name,
        hochgeladen_von_id: nutzer.id,
        company_name: companyName,
    };

    let { data, error } = await supabase.from(TABLE).insert(zeile).select(COLS).single();
    // Zwei Uploads gleichzeitig auf ein leeres Projekt: der zweite verliert
    // das Titelbild-Rennen (partieller Unique-Index) und wird normales Bild.
    if (error && istDublette(error) && wirdTitelbild) {
        ({ data, error } = await supabase.from(TABLE).insert({ ...zeile, titelbild: false }).select(COLS).single());
    }
    if (error || !data) {
        try { await supabase.storage.from(BUCKET).remove([path]); } catch { /* best effort */ }
        return werfe(error || new Error('Bildzeile nicht angelegt.'));
    }
    return mapProjectMediaRow(data as Record<string, unknown>, publicUrlFor);
};

/**
 * Bild loeschen: erst die Zeile (RLS entscheidet), dann die Datei (best
 * effort — fehlt die DELETE-Policy, bleibt das Objekt liegen). War es das
 * Titelbild, rueckt das naechste Bild der Galerie nach.
 */
export const deleteProjectMedia = async (item: ProjectMedia): Promise<void> => {
    const { error } = await supabase.from(TABLE).delete().eq('id', item.id);
    if (error) werfe(error);
    try {
        await supabase.storage.from(BUCKET).remove([item.storagePath]);
    } catch (e) {
        console.warn('[projektbilder] Datei konnte nicht gelöscht werden:', e);
    }
    if (item.titelbild) {
        try {
            const rest = await listProjectMedia(item.projectId);
            const next = rest.items[0];
            if (next && !next.titelbild) {
                await supabase.from(TABLE).update({ titelbild: true }).eq('id', next.id);
            }
        } catch (e) {
            console.warn('[projektbilder] Titelbild nachruecken fehlgeschlagen:', e);
        }
    }
};

/** Titelbild umsetzen: erst dem alten das Haekchen nehmen, dann dem neuen geben. */
export const setProjectMediaTitelbild = async (projectId: string, id: string): Promise<void> => {
    const { error: e1 } = await supabase.from(TABLE).update({ titelbild: false }).eq('project_id', projectId).eq('titelbild', true);
    if (e1) werfe(e1);
    const { error: e2 } = await supabase.from(TABLE).update({ titelbild: true }).eq('id', id);
    if (e2) werfe(e2);
};

/** Bildunterschrift setzen (leer → NULL, auf CAPTION_MAX gekappt). */
export const updateProjectMediaCaption = async (id: string, caption: string): Promise<void> => {
    const text = caption.trim().slice(0, CAPTION_MAX);
    const { error } = await supabase.from(TABLE).update({ bildunterschrift: text || null }).eq('id', id);
    if (error) werfe(error);
};
